import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Typography, Popover, TextField } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { Skeleton } from '@material-ui/lab';
import useKeyBoardEventHandlers from '~/modules/common/hooks/useKeyBoardEventHandlers';
import { NoValue } from '~/modules/common/components';

const descriptionLabel = <FormattedMessage id="customLineItem.description" />;

const useStyles = makeStyles(theme => ({
  skeletonWrapper: {
    width: '100%'
  },
  hover: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer'
    }
  }
}));

const usePopoverStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(1.5),
    width: theme.spacing(31.5)
  }
}));

const DescriptionRenderer = ({
  column: {
    isLineItemUpdating,
    editable,
    onCustomLineItemDescriptionChange,
    onCustomLineItemBlur
  },
  record
}) => {
  const classes = useStyles();
  const popoverClasses = usePopoverStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = useCallback(
    event => {
      if (editable) setAnchorEl(event.currentTarget);
    },
    [editable]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const { handleKeyDown } = useKeyBoardEventHandlers({
    onEnterClick: handleOpen
  });

  const hasValue = !!record.description;

  const descriptionChangeHandler = useCallback(
    e => onCustomLineItemDescriptionChange(e, record),
    [onCustomLineItemDescriptionChange, record]
  );

  const customLineItemBlurHandler = useCallback(
    value => {
      onCustomLineItemBlur(value, record);
      setAnchorEl(null);
    },
    [onCustomLineItemBlur, record]
  );

  if (isLineItemUpdating)
    return (
      <div className={classes.skeletonWrapper}>
        <Skeleton variant="text" width="100%" height={24} />
      </div>
    );

  return (
    <>
      <div
        role="button"
        className={editable ? classes.hover : undefined}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        tabIndex={editable ? 0 : -1}
      >
        {hasValue ? (
          <Typography variant="body2">{record.description}</Typography>
        ) : (
          <NoValue />
        )}
      </div>
      {anchorEl && (
        <Popover
          id="description-editor"
          anchorEl={anchorEl}
          open
          onClose={handleClose}
          classes={popoverClasses}
        >
          <TextField
            id="lineItems.custom.description"
            data-qe-id="lineItems.custom.description"
            variant="filled"
            fullWidth
            label={descriptionLabel}
            onChange={descriptionChangeHandler}
            value={record.description || ''}
            onBlur={customLineItemBlurHandler}
          />
        </Popover>
      )}
    </>
  );
};

DescriptionRenderer.propTypes = {
  column: PropTypes.object.isRequired,
  record: PropTypes.object.isRequired
};

export default DescriptionRenderer;
